import React, { useCallback, useState } from 'react';
import { Alert, Icon } from 'tea-component';
import { useIntl } from 'umi';
import { LANGUAGE_EN } from '@home/_config/config';
import { selectValByCondition } from '@home/utils/common';
import '@home/pages/studio/pr/components/PrNoticeMarquee/index.scss';
import './index.scss';

export interface IStudioTabPrNoticeMarqueeConfig {
  textEn?: string;
  textZh?: string;
}

export interface IStudioTabPrNoticeMarqueeProps {
  tabKey: string;
  prNoticeMarquee?: IStudioTabPrNoticeMarqueeConfig;
}

export function isPrNoticeMarqueeVisible(
  props?: Partial<IStudioTabPrNoticeMarqueeConfig>,
): boolean {
  if (!props) {
    return false;
  }
  return !!(String(props.textEn || '').trim() || String(props.textZh || '').trim());
}

function getStudioTabNoticeDismissKey(tabKey: string) {
  return `dbh.studio.prNoticeMarquee.${tabKey}.dismissed`;
}

function readDismissedFromSession(dismissKey: string): boolean {
  try {
    return (
      typeof sessionStorage !== 'undefined' &&
      sessionStorage.getItem(dismissKey) === '1'
    );
  } catch {
    return false;
  }
}

/**
 * Tab 内容区跑马灯：位于 Tab 下方、筛选器上方；各 Tab 独立文案与关闭状态。
 */
const StudioTabPrNoticeMarquee: React.FC<IStudioTabPrNoticeMarqueeProps> = ({
  tabKey,
  prNoticeMarquee,
}) => {
  const { textEn = '', textZh = '' } = prNoticeMarquee || {};
  const dismissKey = getStudioTabNoticeDismissKey(tabKey);
  const { locale } = useIntl();
  const isEn = locale === LANGUAGE_EN;
  const text = selectValByCondition(isEn, textEn, textZh);
  const visible = isPrNoticeMarqueeVisible(prNoticeMarquee);

  const [dismissed, setDismissed] = useState(() => readDismissedFromSession(dismissKey));

  const handleClose = useCallback(() => {
    try {
      sessionStorage.setItem(dismissKey, '1');
    } catch {
      // ignore
    }
    setDismissed(true);
  }, [dismissKey]);

  if (!visible || dismissed) {
    return null;
  }

  const closeLabel = isEn ? 'Dismiss notice' : '关闭提示';

  return (
    <div className="dbh-studio-tab-notice-marquee">
      <div className="dbh-pr-notice-marquee__wrap">
        <Alert type="info" className="dbh-pr-notice-marquee">
          {text}
        </Alert>
        <button
          type="button"
          className="dbh-pr-notice-marquee__close"
          aria-label={closeLabel}
          title={closeLabel}
          onClick={handleClose}
        >
          <Icon type="close" size="s" />
        </button>
      </div>
    </div>
  );
};

export default StudioTabPrNoticeMarquee;
